import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IFloatingFilterAngularComp } from 'ag-grid-angular';
import { IFloatingFilterParams } from 'ag-grid-community';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  AgTextFilterComponent,
  TextFilterModel,
} from './ag-text-filter.component';

const TYPING_DEBOUNCE_MS = 300;
const NO_VALUE_TYPES = new Set<TextFilterModel['type']>(['blank', 'notBlank']);

/**
 * The header-row box of AgTextFilterComponent (WAP-1646). ag-grid ships a
 * typeable floating filter only for its own filters and falls back to a
 * read-only label for custom ones; this restores type-to-filter: the text goes
 * to the popup's current operator after a short debounce, an empty box clears
 * the filter, and popup edits flow back into the box. Blank / Not blank show
 * their label read-only, since there is no value to type.
 */
@Component({
  selector: 'app-ag-text-floating-filter',
  templateUrl: './ag-text-floating-filter.component.html',
  styleUrls: ['./ag-text-floating-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AgTextFloatingFilterComponent implements IFloatingFilterAngularComp {
  private params!: IFloatingFilterParams<AgTextFilterComponent>;
  private readonly typed$ = new Subject<string>();
  private readonly destroyRef = inject(DestroyRef);

  text = '';
  readonly = false;

  agInit(params: IFloatingFilterParams<AgTextFilterComponent>): void {
    this.params = params;
    this.typed$
      .pipe(
        debounceTime(TYPING_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((text) =>
        params.parentFilterInstance((instance) =>
          instance.applyFromFloating(text),
        ),
      );
  }

  onParentModelChanged(model: TextFilterModel | null): void {
    this.readonly = model != null && NO_VALUE_TYPES.has(model.type);
    let text = '';
    if (model) {
      text = this.readonly ? this.labelOf(model) : (model.filter ?? '');
    }
    this.text = text;
  }

  onInput(value: string): void {
    this.text = value;
    this.typed$.next(value);
  }

  openPopup(): void {
    this.params.showParentFilter();
  }

  private labelOf(model: TextFilterModel): string {
    let label = model.type as string;
    this.params.parentFilterInstance((instance) => {
      label = instance.operatorLabel(model.type);
    });
    return label;
  }
}
